import { getHoseCodesAndInfo, groupDiastereotopicAtomIDs, } from 'openchemlib-utils';
import { predictCOSY } from './predictCOSY';
import { predictCarbon } from './predictCarbon';
import { predictHMBC } from './predictHMBC';
import { predictHSQC } from './predictHSQC';
import { predictProton } from './predictProton';
import { shouldPredict } from './utils/shouldPredict';
const toPredict = {
    cosy: {
        predictor: predictCOSY,
        required: ['H'],
        message: 'Proton prediction is missing so COSY could not be generated.',
    },
    hsqc: {
        predictor: predictHSQC,
        required: ['H', 'C'],
        message: 'Carbon and / or proton prediction is missing so HSQC could not be generated.',
    },
    hmbc: {
        predictor: predictHMBC,
        required: ['H', 'C'],
        message: 'Carbon and / or proton prediction is missing so HMBC could not be generated.',
    },
};
/**
 * Generate the correlation bidimensional nmr peaks based on the number of bonds between a pair of atoms
 * @returns {Promise<object>} - object with molfile, diaIDs, 1D and 2D signals, joined signals, ranges and zones.
 */
export async function predict(molecule, options = {}) {
    const { logger, joinDistance = { H: 0.05, C: 0.5 }, predictOptions = {
        H: {},
        C: {},
        hsqc: {},
        hmbc: {},
        cosy: {},
    }, } = options;
    const hoseCodesAndInfo = getHoseCodesAndInfo(molecule, {
        maxSphereSize: predictOptions?.C?.maxSphereSize,
    });
    const { diaIDs = [], moleculeWithHydrogens, hoses, molfileWithH, distanceMatrix, } = hoseCodesAndInfo;
    const groupedDiaIDs = groupDiastereotopicAtomIDs(diaIDs, moleculeWithHydrogens);
    const protonPrediction = shouldPredict('H', predictOptions)
        ? await predictProton(molfileWithH, {
            ...predictOptions?.H,
            diaIDs,
            distanceMatrix,
        })
        : undefined;
    const carbonPrediction = shouldPredict('C', predictOptions)
        ? await predictCarbon({
            ...predictOptions?.C,
            molfile: molfileWithH,
            groupedDiaIDs,
            hoses,
        })
        : undefined;
    const spectra = {};
    const predictions = {};
    if (check1DPrediction(protonPrediction, diaIDs)) {
        predictions.H = protonPrediction;
        if ('H' in predictOptions)
            spectra.proton = protonPrediction;
    }
    if (check1DPrediction(carbonPrediction, diaIDs)) {
        predictions.C = carbonPrediction;
        if ('C' in predictOptions)
            spectra.carbon = carbonPrediction;
    }
    const optionsTwoD = {
        predictions,
        joinDistance,
        distanceMatrix,
        groupedDiaIDs,
        diaIDs,
    };
    for (const key in predictOptions) {
        if (key === 'H' || key === 'C')
            continue;
        const { predictor, required, message } = toPredict[key];
        if (required.every((key) => key in predictions)) {
            spectra[key] = await predictor(molecule, {
                ...optionsTwoD,
                ...predictOptions[key],
            });
        }
        else if (logger) {
            logger.warn(message);
        }
    }
    return {
        ...hoseCodesAndInfo,
        groupedDiaIDs,
        spectra,
    };
}
function check1DPrediction(prediction, diaIDs = []) {
    if (!prediction)
        return false;
    if (prediction.ranges.length === 0)
        return false;
    const { signals } = prediction;
    for (const signal of signals) {
        const isCorrect = signal.diaIDs?.every((diaID) => diaIDs.includes(diaID));
        if (!isCorrect)
            return false;
    }
    return true;
}
//# sourceMappingURL=predict.js.map